import React from "react";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import { Globe, ChevronDown, Check } from "lucide-react";

import { useTranslation } from "react-i18next";

import "./LanguageSwitcher.css";

const LANGS = [
  {
    value: "en",
    short: "EN",
    label: "English",
  },
  {
    value: "es",
    short: "ES",
    label: "Español",
  },
  {
    value: "ca",
    short: "CA",
    label: "Català",
  },
  {
    value: "nl",
    short: "NL",
    label: "Nederlands",
  },
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();

  const prefersReducedMotion = useReducedMotion();

  const [open, setOpen] = React.useState(false);

  const [currentLang, setCurrentLang] = React.useState(
    (i18n.resolvedLanguage || i18n.language || "en").split("-")[0],
  );

  const ref = React.useRef(null);

  const closeTimerRef = React.useRef(null);

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);

      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = React.useCallback(() => {
    clearCloseTimer();

    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);

      closeTimerRef.current = null;
    }, 180);
  }, [clearCloseTimer]);

  React.useEffect(() => {
    const handleLanguageChanged = (lng) => {
      const normalized = (lng || "en").split(/[-_]/)[0];

      setCurrentLang(normalized);
    };

    i18n.on("languageChanged", handleLanguageChanged);

    handleLanguageChanged(i18n.resolvedLanguage || i18n.language || "en");

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, [i18n]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        clearCloseTimer();

        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [clearCloseTimer]);

  React.useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        clearCloseTimer();

        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [clearCloseTimer]);

  React.useEffect(() => {
    return () => {
      clearCloseTimer();
    };
  }, [clearCloseTimer]);

  const current = LANGS.find((lang) => lang.value === currentLang) || LANGS[0];

  const changeLanguage = async (lang) => {
    try {
      clearCloseTimer();

      await i18n.changeLanguage(lang);

      try {
        localStorage.setItem("language", lang);
      } catch (storageError) {
        console.warn("Could not save language preference:", storageError);
      }

      setOpen(false);
    } catch (error) {
      console.error("Language change failed:", error);
    }
  };

  return (
    <div
      className={`legup-lang ${open ? "is-open" : ""}`}
      ref={ref}
      onMouseEnter={clearCloseTimer}
      onMouseLeave={scheduleClose}
    >
      <motion.button
        type="button"
        className="legup-lang__trigger"
        onClick={(event) => {
          event.stopPropagation();

          clearCloseTimer();

          setOpen((previous) => !previous);
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        whileHover={
          prefersReducedMotion
            ? undefined
            : {
                y: -1,
              }
        }
        whileTap={
          prefersReducedMotion
            ? undefined
            : {
                scale: 0.98,
              }
        }
      >
        <span className="legup-lang__icon-wrap">
          <Globe size={16} />
        </span>

        <span className="legup-lang__short">{current.short}</span>

        <motion.span
          className="legup-lang__chevron"
          animate={{
            rotate: open ? 180 : 0,
          }}
          transition={{
            duration: 0.2,
          }}
        >
          <ChevronDown size={15} />
        </motion.span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="legup-lang__menu"
            role="menu"
            initial={{
              opacity: 0,

              y: prefersReducedMotion ? 0 : -7,

              scale: prefersReducedMotion ? 1 : 0.975,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,

              y: prefersReducedMotion ? 0 : -5,

              scale: prefersReducedMotion ? 1 : 0.985,
            }}
            transition={{
              duration: prefersReducedMotion ? 0.01 : 0.22,

              ease: [0.22, 1, 0.36, 1],
            }}
            onMouseEnter={clearCloseTimer}
          >
            {LANGS.map((lang, index) => {
              const isActive = currentLang === lang.value;

              return (
                <motion.button
                  key={lang.value}
                  type="button"
                  className={`legup-lang__item ${isActive ? "is-active" : ""}`}
                  onClick={() => changeLanguage(lang.value)}
                  role="menuitem"
                  initial={{
                    opacity: 0,

                    x: prefersReducedMotion ? 0 : 7,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  transition={{
                    delay: prefersReducedMotion ? 0 : index * 0.035,

                    duration: 0.24,
                  }}
                >
                  <span className="legup-lang__item-short">{lang.short}</span>

                  <span className="legup-lang__item-label">{lang.label}</span>

                  <span className="legup-lang__item-check">
                    {isActive && <Check size={14} />}
                  </span>
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
